import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ActionSuggestion } from '../lib/api';
import { api } from '../lib/api';

interface Props {
  suggestion: ActionSuggestion;
  onResolved?: () => void;
}

function formatKoreanDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dow = days[d.getDay()];
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h < 12 ? '오전' : '오후';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const mm = m === 0 ? '' : ` ${String(m).padStart(2, '0')}분`;
    return `${month}/${day}(${dow}) ${ampm} ${h12}시${mm}`;
  } catch {
    return iso;
  }
}

export function ActionCard({ suggestion, onResolved }: Props) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'none' | 'approve' | 'reject'>('none');
  const [error, setError] = useState<string | null>(null);

  const approveM = useMutation({
    mutationFn: () => api.approveSuggestion(suggestion.id),
    onMutate: () => setBusy('approve'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      qc.invalidateQueries({ queryKey: ['home-bundle'] });
      setBusy('none');
      onResolved?.();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
      setBusy('none');
    },
  });

  const rejectM = useMutation({
    mutationFn: () => api.rejectSuggestion(suggestion.id),
    onMutate: () => setBusy('reject'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      qc.invalidateQueries({ queryKey: ['home-bundle'] });
      setBusy('none');
      onResolved?.();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : String(e));
      setBusy('none');
    },
  });

  const p = suggestion.payload;
  const isCalNotConnected = error?.includes('calendar-not-connected');

  return (
    <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-xl p-lg shadow-butler-md overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[var(--accent)]" />
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--accent)]">
        제안 · 일정 등록
      </div>
      <h3 className="font-display text-[16px] font-medium text-[var(--text-primary)] mt-sm mb-sm leading-snug">
        {p.title}
      </h3>
      <dl className="text-[13px] text-[var(--text-secondary)] space-y-xs">
        <div className="flex gap-md">
          <dt className="text-[var(--text-muted)] w-[44px] shrink-0">시간</dt>
          <dd>{formatKoreanDateTime(p.start_time)} — {formatKoreanDateTime(p.end_time)}</dd>
        </div>
        {p.location && (
          <div className="flex gap-md">
            <dt className="text-[var(--text-muted)] w-[44px] shrink-0">장소</dt>
            <dd>{p.location}</dd>
          </div>
        )}
        {p.attendees.length > 0 && (
          <div className="flex gap-md">
            <dt className="text-[var(--text-muted)] w-[44px] shrink-0">참석</dt>
            <dd>{p.attendees.map((a) => a.name ?? a.email).filter(Boolean).join(', ')}</dd>
          </div>
        )}
        {p.description && (
          <div className="flex gap-md">
            <dt className="text-[var(--text-muted)] w-[44px] shrink-0">메모</dt>
            <dd>{p.description}</dd>
          </div>
        )}
      </dl>
      {suggestion.sourceQuote && (
        <p className="mt-md pt-md border-t border-[var(--border)] text-[11px] text-[var(--text-muted)] leading-relaxed">
          {suggestion.sourceQuote}
        </p>
      )}
      <div className="flex gap-sm mt-md">
        <button
          onClick={() => approveM.mutate()}
          disabled={busy !== 'none'}
          className="flex-1 py-[10px] rounded-md bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] text-[13px] font-medium disabled:opacity-50 transition-colors"
        >
          {busy === 'approve' ? '등록 중…' : '등록'}
        </button>
        <button
          disabled={busy !== 'none'}
          className="flex-1 py-[10px] rounded-md border border-[var(--border)] bg-[var(--surface-elev)] text-[var(--text-secondary)] text-[13px] font-medium disabled:opacity-50"
          onClick={() => alert('편집 UI는 다음 단계 — 일단 등록/건너뛰기만.')}
        >
          수정
        </button>
        <button
          onClick={() => rejectM.mutate()}
          disabled={busy !== 'none'}
          className="flex-1 py-[10px] rounded-md text-[var(--text-secondary)] text-[13px] font-medium disabled:opacity-50"
        >
          {busy === 'reject' ? '...' : '건너뛰기'}
        </button>
      </div>
      {error && (
        <div className="mt-md text-[12px] text-[var(--error)] bg-[var(--surface-elev)] border border-[var(--border)] rounded-md p-sm">
          {isCalNotConnected
            ? '캘린더가 아직 연결되지 않았어요. 더보기 → 캘린더 연동에서 Google 캘린더를 연결해주세요.'
            : `오류: ${error}`}
        </div>
      )}
    </div>
  );
}
